import { DateFormat, formatDate } from "@nova/format"
import { useTranslation } from "@nova/use-translation"
import classNames from "classnames"
import {
    animationClass,
    defaultFolderName,
    WorkspaceTreeMaxDepth,
} from "features/data-management/workspace/constants"
import { WorkspaceEntityType, WorkspaceFolderProps } from "features/data-management/workspace/types"
import { useCallback, useEffect, useMemo, useRef, useState } from "react"
import {
    ArrowRight,
    Icon,
    PersonAdd,
    SmallIcon,
    Star,
    StarFill,
    ThreeDotsSettings,
} from "@nova/component-icons"
import { useAppDispatch, useAppSelector } from "integrations/redux/hooks"
import { ContextMenu } from "features/data-management/workspace/TODO-put-in-package/ContextMenu"
import { ClickAwayListener } from "@nova/component-click-away-listener"
import { RenamableText } from "features/data-management/workspace/TODO-put-in-package/RenamableText"
import { createContextMenuSections } from "features/data-management/workspace/module"
import { selectIdRenaming } from "features/data-management/workspace/WorkspaceManagementSlice"
import { useWorkspacePermissions } from "features/data-management/workspace/useWorkspacePermissions"
import { useWorkspaceFolderDragAndDrop } from "../../../useWorkspaceFolderDragAndDrop"
import { ButtonPrimary } from "@nova/component-btn"
import { setActiveContextMenu } from "features/popups/slice"
import { selectActiveContextMenu } from "features/popups/contextMenu"
import { useRouter } from "pages"

export const WorkspaceBodyFolderListItem = ({
    folder,
    tagChain,
    isFavorite,
    handleMove,
    handleCreateFolder,
    handleBeginRenaming,
    handleConfirmRenaming,
    handleCancelRenaming,
    handleOpenShareModal,
    handleToggleFavorite,
    handleDelete,
}: WorkspaceFolderProps) => {
    // Need this since multiple entities with the same ID can exist on the screen in parallel.
    const key = `list_${folder.id}`
    const type = WorkspaceEntityType.Folder

    const { t } = useTranslation()
    const dispatch = useAppDispatch()
    const [isHovering, setIsHovering] = useState(false)
    const { isEditor } = useWorkspacePermissions(folder.id)
    const activeListFolderContextMenu = useAppSelector(selectActiveContextMenu("ListFolder"))
    const contextMenuIsForThisListItem = activeListFolderContextMenu?.data.folderId === folder.id
    const renameRef = useRef<HTMLInputElement>(null)
    const idRenaming = useAppSelector(selectIdRenaming())
    const isRenaming = idRenaming === key
    const canCreateFolder = tagChain.length <= WorkspaceTreeMaxDepth

    const contextMenuSections = useMemo(
        () =>
            createContextMenuSections({
                id: folder.id,
                key,
                title: folder.name,
                isFavorite,
                onCreateFolder: isEditor && canCreateFolder ? handleCreateFolder : undefined,
                onToggleFavorite: handleToggleFavorite,
                onBeginRenaming: isEditor ? handleBeginRenaming : undefined,
                // onMoveTo: ,
                onOpenShareModal: handleOpenShareModal,
                onDelete: isEditor ? (id, name) => handleDelete(id, name, type) : undefined,
            }),
        [
            folder,
            key,
            type,
            isFavorite,
            isEditor,
            canCreateFolder,
            handleCreateFolder,
            handleToggleFavorite,
            handleBeginRenaming,
            handleOpenShareModal,
            handleDelete,
        ]
    )

    const { dragAndDropRef, isOver, canDrop, isDragging, anythingIsDragging } =
        useWorkspaceFolderDragAndDrop({
            id: folder.id,
            subFolders: folder.subTags,
            isRenaming,
            isEditor,
            handleMove,
        })

    // Cancel renaming on clickaway.
    const handleClickAway = () => {
        if (idRenaming) handleCancelRenaming?.()
    }

    // Focus the text field when renaming begins.
    useEffect(() => {
        if (isRenaming) renameRef.current?.focus()
    }, [isRenaming])

    /**
     * @param force
     * If `true`, function will force-show the context menu.
     * If `false`, function will force-hide the context menu.
     * If `undefined`, function will toggle the context menu.
     */
    const handleToggleContextMenu = (force?: boolean) => {
        const shouldHide = (contextMenuIsForThisListItem && !force) || force === false
        if (force === false) setIsHovering(false)
        dispatch(
            setActiveContextMenu(
                shouldHide
                    ? undefined
                    : {
                          type: "ListFolder",
                          data: { folderId: folder.id, fixedPosition: false },
                          position: { top: 0, left: 0 },
                      }
            )
        )
    }

    const shouldShow3DotsButton =
        (isHovering || contextMenuIsForThisListItem) &&
        contextMenuSections.length > 0 &&
        !isRenaming

    const shouldEmphasizeRow = isHovering || contextMenuIsForThisListItem

    const router = useRouter("/workspace/:workspaceId")
    const handleOpenFolder = useCallback(() => {
        router.navigate({ path: "/explore/ws/*", splat: tagChain.join("/") })
    }, [router, tagChain])

    return (
        <ClickAwayListener onClickAway={handleClickAway}>
            <div
                ref={dragAndDropRef}
                className={classNames("h-10 px-2 flex items-center rounded-lg border relative", {
                    "border-transparent": !shouldEmphasizeRow && !isOver,
                    "border-gray-700": shouldEmphasizeRow && !isOver,
                    "border-blue-500": isOver && canDrop,
                    "border-red-500": isOver && !canDrop,
                    "opacity-20": (!canDrop && anythingIsDragging) || isDragging,
                })}
                onMouseEnter={() => setIsHovering(true)}
                onMouseLeave={() => setIsHovering(false)}
                onDoubleClick={!isRenaming ? () => handleOpenFolder() : (e) => e.stopPropagation()}
                onContextMenu={(e) => {
                    e.preventDefault()
                    e.stopPropagation()
                    dispatch(
                        setActiveContextMenu({
                            type: "ListFolder",
                            data: {
                                folderId: folder.id,
                                fixedPosition: true,
                            },
                            position: { top: e.clientY, left: e.clientX },
                        })
                    )
                }}
            >
                <div className="flex-[4] flex items-center gap-2 truncate">
                    <RenamableText
                        ref={renameRef}
                        Tag="h5"
                        title={folder.name}
                        id={folder.id}
                        isRenaming={isRenaming}
                        initialTitleIsPlaceholder={folder.name === defaultFolderName}
                        isFullWidth
                        className={classNames(
                            animationClass,
                            "!flex-shrink text-gray-0 !h-6 pr-4",
                            {
                                "hover:text-blue-500": !isRenaming,
                            }
                        )}
                        onClick={
                            !isRenaming ? () => handleOpenFolder() : (e) => e.stopPropagation()
                        }
                        onConfirmRenaming={(id, name) => handleConfirmRenaming(id, name, type)}
                        onCancelRenaming={handleCancelRenaming}
                    />
                    {isFavorite && !isRenaming && (
                        <SmallIcon Icon={StarFill} className="text-gray-0" />
                    )}
                </div>
                <>
                    <h5 className="flex-[1] pointer-events-none">
                        {t(
                            folder.updatedAt
                                ? formatDate({
                                      value: folder.updatedAt,
                                      format: DateFormat.MMMDDYYYY,
                                  })
                                : ""
                        )}
                    </h5>
                    <h5 className="flex-[1] pointer-events-none">
                        {t(
                            folder.createdAt
                                ? formatDate({
                                      value: folder.createdAt,
                                      format: DateFormat.MMMDDYYYY,
                                  })
                                : ""
                        )}
                    </h5>
                </>
                {shouldEmphasizeRow && (
                    <div className="flex gap-4 items-center absolute right-2 top-50% pl-[10rem] bg-gradient-to-l from-gray-800">
                        {/* Open Button */}
                        <ButtonPrimary
                            title="Open"
                            RightIcon={ArrowRight}
                            height="1.5rem"
                            onClick={() => handleOpenFolder()}
                        />

                        {/* Right-Side Buttons */}
                        <div className="flex gap-1 items-center text-gray-100 relative">
                            {/* Add to / Remove from Favorites */}
                            <div
                                onClick={() => handleToggleFavorite(folder.id)}
                                onDoubleClick={(e) => e.stopPropagation()}
                            >
                                {!isFavorite ? (
                                    <Icon
                                        Icon={Star}
                                        className={classNames(
                                            animationClass,
                                            "hover:text-gray-0 cursor-pointer"
                                        )}
                                    />
                                ) : (
                                    <Icon
                                        Icon={StarFill}
                                        className={classNames(
                                            animationClass,
                                            "text-yellow-500 hover:text-yellow-200 cursor-pointer"
                                        )}
                                    />
                                )}
                            </div>

                            {/* Share Button */}
                            <Icon
                                Icon={PersonAdd}
                                className={classNames(
                                    animationClass,
                                    "ml-1 hover:text-gray-0 cursor-pointer"
                                )}
                                onClick={() => handleOpenShareModal?.(folder.id)}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />

                            {/* 3 Dots Button */}
                            <Icon
                                Icon={ThreeDotsSettings}
                                className={classNames(
                                    animationClass,
                                    "hover:text-gray-0 cursor-pointer",
                                    {
                                        "opacity-0": !shouldShow3DotsButton,
                                    }
                                )}
                                onClick={(e) => {
                                    e.stopPropagation()
                                    handleToggleContextMenu()
                                }}
                                onDoubleClick={(e) => e.stopPropagation()}
                            />

                            {/* Context Menu */}
                            {contextMenuIsForThisListItem && (
                                <ContextMenu
                                    sections={contextMenuSections}
                                    onBlur={() => handleToggleContextMenu(false)}
                                    className="flex-none"
                                    isFixedPosition={activeListFolderContextMenu.data.fixedPosition}
                                    style={
                                        !activeListFolderContextMenu.data.fixedPosition
                                            ? { top: "1.5rem", right: "0.25rem" }
                                            : undefined
                                    }
                                    top={activeListFolderContextMenu.position.top}
                                    left={activeListFolderContextMenu.position.left}
                                />
                            )}
                        </div>
                    </div>
                )}
            </div>
        </ClickAwayListener>
    )
}
